<template>
  <div v-if="visible" data-theme="light">
    <!-- 顶部推广栏：已安装应用时显示 -->
    <div v-if="isInstalled" class="app-promotion app-promotion--top">
      <div class="app-promotion__content">
        <div class="app-promotion__info">
          <img :src="config.topLogo" :alt="t('appPromotion.logoAlt')" class="app-promotion__topLogo" />
          <div class="app-promotion__text">
            <TextCarousel :texts="carouselTexts" class="app-promotion__topSubtitle CH_8_Bold" />
          </div>
        </div>
        <div class="wx-relative">
          <button class="app-promotion__button btn_md primary_btn EN_4_Bold" @click="handleAction">
            {{ t('appPromotion.openApp') }}
          </button>
          <wxOpenApp v-if="isWeiXin" :appUrl="props.appData.open_app_url" :launch_app_id="props.appData.appId"></wxOpenApp>
        </div>
      </div>
    </div>

    <!-- 底部推广栏：未安装应用时显示 -->
    <div v-else class="app-promotion app-promotion--top">
      <div class="app-promotion__content">
        <div class="app-promotion__info">
          <img :src="config.bottomLogo" :alt="t('appPromotion.logoAlt')" class="app-promotion__bottomLogo" />
          <div class="app-promotion__text">
            <TextCarousel :texts="carouselTexts" class="app-promotion__bottomSubtitle CH_7_Bold" />
          </div>
        </div>
        <div class="wx-relative">
          <button class="app-promotion__button btn_md black_btn EN_4_Bold" @click="handleAction">
            {{ t('appPromotion.downloadApp') }}
          </button>
          <wxOpenApp v-if="isWeiXin" :appUrl="props.appData.open_app_url" :launch_app_id="props.appData.appId"></wxOpenApp>
        </div>
      </div>
    </div>

    <div class="app-promotion__spacer" style="height: 52px;"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDeviceInfo } from '@/composables/useDeviceInfo';
import { useAppDetection } from '@/composables/useAppDetection';
import TextCarousel from '@/components/TextCarousel.vue';
import topLogo from '@/assets/images/font-logo.svg';
import bottomLogo from '@/assets/images/bg-logo.svg';
import { launchApp, setWeiXin } from '@/utils/version';
import wxOpenApp from '@/components/wxOpenApp.vue';

const { t } = useI18n();
const { isWechat } = useDeviceInfo();
const visible = ref(true);

const props = defineProps({
  appData: {
    type: Object,
    required: true,
    default: () => ({})
  }
});

// 轮播文案配置
const carouselTexts = [
  t('appPromotion.cardTrade'),
  t('appPromotion.topDeck'),
  t('appPromotion.priceEvaluation'),
  t('appPromotion.cardCommunity'),
  t('appPromotion.gameTeaching')
];

// 应用配置
const config = {
  topLogo: topLogo,
  bottomLogo: bottomLogo,
  appUrl: props.appData.open_app_url,
  downloadUrl: 'https://jihuanshe.com/app/download',
  scheme: 'jihuanshe://',
  universalLink: 'https://jihuanshe.com/app'
};

// 使用应用检测
const { isInstalled, isChecking, checkAppInstalled } = useAppDetection({
  scheme: config.scheme,
  universalLink: config.universalLink,
  timeout: 2000
});

const isWeiXin = computed(() => {
  return isWechat.value;
});

const handleSetWeiXin = (data: any) => {
  if (isWeiXin.value) setWeiXin(data);
};

// 控制body padding
const updateBodyPadding = (installed: boolean) => {
  const body = document.body;
  if (installed) {
    // 已安装应用，添加顶部padding
    body.style.paddingTop = '52px';
    body.style.paddingBottom = '0';
  } else {
    // 未安装应用，添加底部padding
    body.style.paddingTop = '0';
    body.style.paddingBottom = '52px';
  }
};

// 监听isInstalled状态变化
watch(isInstalled, (newValue) => {
  if (newValue !== null) {
    updateBodyPadding(newValue);
  }
});

watch(() => props.appData, (newVal) => {
  if (newVal && Object.keys(newVal).length > 0) {
    console.log(newVal, 'newVal');
    handleSetWeiXin(newVal);
  }
}, { immediate: true });

// 组件卸载时清除padding
onUnmounted(() => {
  document.body.style.paddingTop = '0';
  document.body.style.paddingBottom = '0';
});

// 处理按钮点击
const handleAction = () => {
  launchApp(props.appData.open_app_url);
};

onMounted(() => {
  checkAppInstalled();
  // 增加初始化时的微信处理
  console.log(props.appData, 'props.appData');
  if (props.appData && Object.keys(props.appData).length > 0) {
    handleSetWeiXin(props.appData);
  }
});
</script>

<style scoped lang="scss">
/* 微信父级按钮必须添加该class */
.wx-relative {
  position: relative;
}

.app-promotion {
  position: fixed;
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--container-surface-white);
  padding: var(--padding-7) var(--padding-9);
  
  &--top {
    top: 0;
  }
  
  &--bottom {
    bottom: 0;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
  }

  &__top-spacer {
    height: 52px;
  }

  &__bottom-spacer {
    height: 52px;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: var(--gap-7);
  }

  &__topLogo {
    width: 74px;
    height: 24px;
  }

  &__bottomLogo {
    width: 70px;
    height: 32px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 120px;
  }


  &__bottomSubtitle {
    font-size: 12px;
    color: var(--color-text-secondary);
    margin: 0;
  }

  &__button {
    outline: none;
    box-shadow: none;
    border: none;
  }

}

// 为顶部推广栏添加页面内容偏移
:deep(.deck-share) {
  .app-promotion--top + & {
    margin-top: 64px;
  }
}
</style> 